import * as Accountant from 'app/calcs/Accountant'
import { NEBFPensionCalculations } from 'app/calcs/customers/NEBF'
import { makeNEBFComputer } from 'app/calcs/customers/NEBF/Factory';
import { INEBFPensionInputs, NEBFFormOfPayment } from 'app/calcs/customers/NEBF/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { Infobox } from 'app/components/Infobox';
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { cachedLocal697ExportExhibitTab } from 'app/redux/Customers/local697/actions';
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo';
import * as utils from 'app/utils'
import * as Config from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

enum ExhibitGenerationState {
  idle, generating, ready
}

interface INEBFLocal697PensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: string
  exhibitGeneration: ExhibitGenerationState
  downloadFilename: string

  // these are synced with profile.pension.NEBF
  beginPaymentsAge: moment.Duration
  formOfPayment: SimplePensionFormOfPayment
  currentYearsService: moment.Duration
  hoursWorked: number
}

class NEBFLocal697PensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, INEBFLocal697PensionContainerState> {
  beginPaymentsAgeNS = 'pension.NEBF.beginPaymentsAge'
  hoursWorkedNS = 'pension.shared.hoursWorked'
  currentYearsServiceNS = 'pension.NEBF.currentYearsService'
  formOfPaymentNS = 'pension.NEBF.formOfPayment'

  state: INEBFLocal697PensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    currentYearsService: this.getValueForQuestion(this.currentYearsServiceNS) || null,
    hoursWorked: this.getValueForQuestion(this.hoursWorkedNS) || 0,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null,
    exhibitGeneration: ExhibitGenerationState.idle,
    downloadFilename: null
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.NEBF
    const { beginPaymentsAge } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = makeNEBFComputer(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
      .then((payout) => {
        const monthlyPayout = utils.toCurrency(payout.amount)
        this.setState({ monthlyPayout })
      })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.NEBF) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: INEBFLocal697PensionContainerState) {
    this.updateCalculations(nextState)
  }

  onExportExhibits() {
    this.setState({ exhibitGeneration: ExhibitGenerationState.generating })
    const profile = this.state.profile
    const member = profile.getHeadOfHousehold()
    const spouse = profile.getSpouse()
    const shared = profile.pension.shared
    const vals = profile.pension.local697
    const ioInputs = profile.pension.IO
    const nebfInputs = profile.pension.NEBF
    cachedLocal697ExportExhibitTab({ member, spouse, vals, ioInputs, nebfInputs, shared, when: null })
      // NOTE: I don't know why this comes back as a workbook response
      // that has 'message' at the wrong level....
      .then((xx: any) => {
        this.setState({ exhibitGeneration: ExhibitGenerationState.ready })
        const endpoint = Config.getWebServicesConfig().miscEndpoint
        const downloadFilename = endpoint + 'api/misc/downloadTransactionFile?path=' + xx.Message
        this.setState({ downloadFilename })
      })
  }

  updateCalculations(nextState: INEBFLocal697PensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, currentYearsService, beginPaymentsAge } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment,
      currentYearsService
    }
    const newNEBF = _.defaults({}, updates, profile.pension.NEBF)

    if (!this.initCalc || !_.isEqual(profile.pension.NEBF, newNEBF)) {
      this.initCalc = true
      profile.pension.NEBF = newNEBF
      this.debounceCalculations()
    }
  }

  updateHoursWorked(hoursWorked: number) {
    this.state.profile.pension.shared.hoursWorked = hoursWorked
    this.setStateAndSave({ hoursWorked })
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.NEBF.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateRetirementAge(deltaYears: number) {
    const NEBF = this.state.profile.pension.NEBF
    NEBF.beginPaymentsAge.add(deltaYears, 'years')

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateYearsOfService(yos: number) {
    const currentYearsService = moment.duration(yos, 'years')
    this.state.profile.pension.NEBF.currentYearsService = currentYearsService
    this.setState({ currentYearsService })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  renderExhibitGeneration() {
    switch (this.state.exhibitGeneration) {
      case ExhibitGenerationState.idle: {
        return <div>
          For information on benefits at other projected ages, click&nbsp;
          <a onClick={() => { this.onExportExhibits() }}>here</a> to prepare the analysis.
          </div>
      }
      case ExhibitGenerationState.generating: {
        return <div>
          Please wait while your report is being generated.
        </div>
      }
      case ExhibitGenerationState.ready:
      default: {
        return <div>
          Your report is ready for download. Click to <a href={this.state.downloadFilename}>here</a> to download it.
        </div>
      }
    }
  }

  render() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.NEBF || !this.state.beginPaymentsAge) { return null }

    // RA-284/FF-116 everyone is eligible
    //const nebfEligible = profile.pension.local697.planPEligible
    const nebfEligible = true

    if (nebfEligible) {
      return this.renderFull()
    } else {
      return <section className="animated fadeIn">
        <h1>NEBF Pension</h1>
        <h5 className="lead">Based on our records, you are not eligible for benefits under NEBF.
          If you think this is incorrect, please contact us using the information <a href="#/support">here</a>.
          </h5>
      </section>
    }

  }

  renderFull() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.NEBF || !this.state.beginPaymentsAge) { return null }

    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = (this.state.monthlyPayout !== '$0')
    const censusDateText = theCensusInfo.censusDate.format('LL')
    const nebfServiceYearsText = utils.toNiceAge(profile.pension.NEBF.currentYearsService, { yearsUnits: 'years' })

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter([
      NEBFFormOfPayment.lifeOnly,
      SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
      SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor
    ], findFop)

    return (
      <section className="animated fadeIn">
        <h1>NEBF Pension</h1>
        <h5 className="lead">We have the following information from your records.
          This information is current as of {censusDateText}. {theCensusInfo.publicNotes}
          Note the I.B.E.W. Local 697 Benefits Office does not see your employer&rsquo;s NEBF
          contributions; as such, the following estimate was calculated utilizing the
          hours contributed to the Local 697 Pension Plan.
        </h5>
        <ul>
          <li>NEBF Years of Credit: <a>{nebfServiceYearsText}</a></li>
        </ul>
        <Row>
          <Col md={6}>

            <FormGroup>
              <RetirementAgeSelector min={60} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={allowedFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary &&
              <h2 className="animated fadeIn">
                If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
                                                                              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>
            }

          </Col>
        </Row>

        <Row>
          <Col md={6}>
            {this.renderExhibitGeneration()}
          </Col>
        </Row>
      </section >
    )
  }
}

const NEBFLocal697PensionQuestionsGroup = () => (
  <NEBFLocal697PensionContainer
    header="NEBF Pension" icon="" />
)

export {
  NEBFLocal697PensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Pension/NEBF/Local697/index.tsx